import { Box, Button, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import {
  exportClientEchallan,
  getClientEchallan,
  getClientForm,
  getIncomeTaxProfile,
} from "api/services/automation/income-tax";
import Table, { ColumnType } from "components/Table";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ResType } from "types";
import { StyledSpan } from "./styles";
import { useState } from "react";
import AccessDenied from "./Access/accessDenide";
import Loader from "components/Loader";
import { formatToRupeess } from "utils/formattedAmount";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import moment from "moment";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { handleError } from "utils/handleError";
import { snack } from "components/toast";
import { getCurrentFormattedDateTime } from "utils";
import sortHandler from "components/Table/SortHandler";
import _ from "lodash";
import { SortDirection } from "data/constants";
import { getAutFinancialYears, sortFilter } from "pages/atom-pro/constants";
import SearchContainer from "components/SearchContainer";

const ClientChallan = (state) => {
  const params = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [checkAccess, setCheckAccess] = useState(true);
  const [sort, setSort] = useState<any>({});
  const [assessmentYear, setAssessmentYear] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [sortValue, setSortValue] = useState<any>(null);

  const { data, isLoading }: ResType = useQuery(
    [
      "get-e-challan",
      params.incometaxid,
      {
        limit: pageCount,
        offset: page * pageCount,
        sort,
        search,
        sortValue,
        assessmentYear,
      },
    ],
    getClientEchallan,
    {
      onSuccess: (res) => {
        setCheckAccess(res?.data?.accessDenied);
        setTotalCount(res?.data?.count);
      },
    }
  );

  const onAssessmentYearChange = (e: any) => {
    setAssessmentYear(e.target.value);
    setPage(0);
  };

  const onSortChange = (e: any) => {
    setPage(0);
    setSortValue(e.target.value);
  };

  const handleSearch = (e: any) => {
    setPage(0);
    setSearch(e);
  };

  function calculateAssessmentYear(startYear) {
    const endYear = parseInt(startYear) + 1;
    return startYear + "-" + endYear.toString().slice(2);
  }
  const defaultColumns: Array<ColumnType> = [
    {
      key: "id",
      title: "AY",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Box>
            <Typography>{row?.assessmentYear ? `${row?.assessmentYear}` : ""}</Typography>
          </Box>
        );
      },
    },
    {
      key: "minorDesc",
      title: "Type of Payment",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Box>
            <Typography>
              {row?.minorDesc ? `${row?.minorDesc} (${row?.minorHead})` : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: "paymentTime",
      title: "Date of Payment",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Typography>
            {row?.paymentTime ? moment(row?.paymentTime).format("MMM DD, YYYY") : "-"}
          </Typography>
        );
      },
    },
    {
      key: "acin",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      title: (
        <Box display="flex" alignItems="center">
          <Typography>Alternate CIN</Typography>
          <Tooltip title="Alternate CIN has first 7 digits as BSR code of bank followed by date of payment in 8 digits and further followed by 5 digit challan number">
            <HelpOutlineIcon fontSize="small" sx={{ marginLeft: "5px" }} />
          </Tooltip>
        </Box>
      ),
    },
    {
      key: "totalAmt",
      title: "Total Amount",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      render: (row: any) => {
        return (
          <Tooltip
            title={`Tax: ${row?.basicTax} + Surcharge: ${row?.surCharge} + Cess: ${
              row?.eduCess
            } + Interest: ${row?.interest} + Penalty: ${row?.penalty} + Others: ${
              row?.others
            } = Total Amount: ${formatToRupeess(row?.totalAmt)}`}
          >
            <Typography>
              <Box display="flex" alignItems="center">
                <CurrencyRupeeIcon sx={{ width: "15px" }} /> {formatToRupeess(row?.totalAmt)}
              </Box>
            </Typography>
          </Tooltip>
        );
      },
    },
  ];
  const [columns, setColumns] = useState(_.cloneDeep(defaultColumns));
  const handleSort = (v: any) => {
    sortHandler({
      key: v.key,
      columns,
      sortState: sort,
      setSortState: setSort,
      setColumns: setColumns,
    });
  };
  const handleRowClick = (v: any) => {
    navigate(`/atom-pro/income-tax/incometax/${params.incometaxid}/e-challan/${v?.id}`);
  };

  const { data: profiledata, isLoading: isprofileloading }: ResType = useQuery(
    ["get-income-tax-profile", params.incometaxid],
    getIncomeTaxProfile
  );
  const clientName = profiledata?.data?.profileDetails?.name;
  const { mutate: mutate1 } = useMutation(exportClientEchallan, {
    onSuccess: (res: any) => {
      const arr = new Uint8Array(res.data?.data);
      const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const file = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      const formattedDateTime = getCurrentFormattedDateTime();
      link.href = file;
      link.download = `ATOM Pro - ${clientName} Income Tax (PAN) Challans ${formattedDateTime}.xlsx`;
      link.click();
      snack.success("Income Tax (PAN) challans exported sucessfully");
    },
    onError: (err: any) => {
      snack.error(err);
      snack.error(handleError(err));
    },
  });

  const handleExport = () => {
    mutate1({
      incometaxid: params.incometaxid,
      ...state,
    });
  };

  if (isLoading) return <Loader />;
  return (
    <>
      <Box m={1}>
        {!checkAccess && <AccessDenied />}
        <Box display="inline-flex" m={1}>
          <Box ml={1}>
            <TextField
              name="assessmentYear"
              value={assessmentYear}
              onChange={onAssessmentYearChange}
              size="small"
              label="Assessment Year"
              select
              sx={{ width: 150 }}
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              <MenuItem key={""} value={"null"}>
                NA
              </MenuItem>
              {getAutFinancialYears().map((year: string) => (
                <MenuItem key={year} value={year.split("-")[0]}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box ml={1}>
            <TextField
              name="sortValue"
              value={sortValue}
              onChange={onSortChange}
              size="small"
              label="Sort By"
              select
              sx={{ width: 100 }}
            >
              <MenuItem key={""} value={""}>
                Select
              </MenuItem>
              {sortFilter.map((sort) => (
                <MenuItem key={sort.value} value={sort.value}>
                  {sort.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box ml={1}>
            <SearchContainer
              minWidth="430px"
              debounced
              value={search}
              placeHolder="Search by CIN | Type of Payment"
              onChange={handleSearch}
            />
          </Box>
          <Box ml={1}>
            <Button
              onClick={() => {
                setAssessmentYear(null);
                setSortValue(null);
                setSearch("");
                setInterval("");
              }}
              size="medium"
              color="secondary"
              variant="outlined"
              // sx={{ width: 120 }}
            >
              Clear Filters
            </Button>
          </Box>
          <Box display="flex" justifyContent="flex-end" ml={1}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleExport}
              startIcon={<FileDownloadOutlinedIcon />}
            >
              Export
            </Button>
          </Box>
        </Box>

        {checkAccess && (
          <Box>
            {data && data?.data.count > 0 ? (
              <>
                <Table
                  sx={{ pb: 0, mb: 0, height: "470px" }}
                  // selection={{ selected, setSelected }}
                  pagination={{ totalCount, pageCount, setPageCount, page, setPage }}
                  data={data?.data?.result || []}
                  columns={columns}
                  sortHandler={handleSort}
                  loading={isLoading}
                  onRowClick={handleRowClick}
                />
              </>
            ) : (
              <>
                <Box textAlign="center" mt={20}>
                  <Typography variant="subtitle1" color="rgba(0,0,0,0.5)">
                    No Data present
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default ClientChallan;
